import { useNavigate } from "react-router-dom";
import { ArrowLeft, Workflow } from "lucide-react";

import { Tabs, TabsList, TabsTrigger } from "@/app/admin/_common/ui/tabs";
import { Badge } from "@/app/admin/_common/ui/badge";
import { cn } from "@/app/_utils/cn";
import { tabTriggerClassName } from "@/app/admin/shows/[show_id]/_cmp/ShowDetailHeader";
import type { Automation } from "@/app/_services/AutomationService";

import {
  MODE_BADGE_CLASSES,
  MODE_DOT_CLASSES,
  MODE_LABEL,
  STATUS_BADGE_CLASSES,
  STATUS_DOT_CLASSES,
  STATUS_LABEL,
  toAutomationMode,
  toAutomationStatus,
} from "../../_cmp/automationStatus";

export type DetailTab = "setup" | "runs" | "template";

export default function AutomationDetailHeader({
  automation,
  tab,
  onTabChange,
}: {
  automation: Automation | null;
  tab: DetailTab;
  onTabChange: (tab: DetailTab) => void;
}) {
  const navigate = useNavigate();
  const status = toAutomationStatus(automation?.status);
  const mode = toAutomationMode(automation?.mode);

  return (
    <div className="flex flex-col gap-3 border-b border-border bg-card px-5 pt-4">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => navigate("/admin/tools/automations")}
          className="flex size-6 flex-none items-center justify-center rounded-md text-muted-foreground hover:bg-muted"
          aria-label="Back to automations"
        >
          <ArrowLeft className="size-4" />
        </button>

        {/* my-0 is load-bearing: Tailwind preflight is disabled here, so an <h1> keeps
            the UA default margin. */}
        <h1 className="my-0 flex min-w-0 items-center gap-2 truncate py-0.5 text-[21px] font-bold leading-none tracking-tight text-foreground">
          <Workflow className="size-5 flex-none text-foreground" />
          {automation?.name ?? "Automation"}
        </h1>

        <div className="ml-1 flex flex-none items-center gap-1.5">
          <Badge
            variant="outline"
            className={cn("gap-1.5 rounded-[7px] font-normal", STATUS_BADGE_CLASSES[status])}
          >
            <span className={cn("size-1.5 flex-none rounded-full", STATUS_DOT_CLASSES[status])} />
            {STATUS_LABEL[status]}
          </Badge>
          <Badge
            variant="outline"
            className={cn("gap-1.5 rounded-[7px] font-normal", MODE_BADGE_CLASSES[mode])}
          >
            <span className={cn("size-1.5 flex-none rounded-full", MODE_DOT_CLASSES[mode])} />
            {MODE_LABEL[mode]}
          </Badge>
        </div>
      </div>

      {/* Local-state tabs: these are views on one route, not separate routes, so the
          URL does not change. Matches LinkDetailHeader rather than ShowDetailHeader. */}
      <Tabs value={tab} onValueChange={(next) => onTabChange(next as DetailTab)}>
        <TabsList className="h-auto w-full justify-start gap-4 rounded-none border-0 bg-transparent p-0">
          <TabsTrigger value="setup" className={tabTriggerClassName}>
            Setup
          </TabsTrigger>
          <TabsTrigger value="runs" className={tabTriggerClassName}>
            Runs
          </TabsTrigger>
          <TabsTrigger value="template" className={tabTriggerClassName}>
            Template
          </TabsTrigger>
        </TabsList>
      </Tabs>
    </div>
  );
}
